import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaNewspaper, FaFutbol, FaGlobe, FaChartLine, FaTheaterMasks,
  FaArrowLeft, FaPlay, FaCheck, FaSpinner, FaLock, FaInfoCircle,
  FaUserTie, FaGlobeAfrica
} from 'react-icons/fa';
import AppHeader from '../components/AppHeader';
import api from '../utils/api';

// Mapping des icônes par catégorie
const CATEGORY_ICONS = {
  france: FaNewspaper,
  afrique_ouest: FaGlobeAfrica,
  sport: FaFutbol,
  international: FaGlobe,
  economie: FaChartLine,
  culture: FaTheaterMasks,
  qui_est_qui_fr: FaUserTie,
  qui_est_qui_ao: FaUserTie
};

// Couleurs par catégorie
const CATEGORY_COLORS = {
  france: '#1B4965',
  afrique_ouest: '#E67E22',
  sport: '#10B981',
  international: '#6366F1',
  economie: '#F59E0B',
  culture: '#EC4899',
  qui_est_qui_fr: '#8B5CF6',
  qui_est_qui_ao: '#D97706'
};

function QuizSelectionPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState({});
  const [countries, setCountries] = useState({});
  const [availableQuizzes, setAvailableQuizzes] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('FR');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadQuizData();
  }, []);

  const loadQuizData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, availableRes] = await Promise.all([
        api.get('/quiz/categories'),
        api.get('/quiz/available')
      ]);
      
      setCategories(categoriesRes.data.categories || {});
      setCountries(categoriesRes.data.countries || {});
      setAvailableQuizzes(availableRes.data || {});
    } catch (err) {
      setError('Impossible de charger les quiz');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (!selectedCategory) return;
    
    // Pour les quiz statiques (qui_est_qui_ao), pas besoin de pays
    const isStaticQuiz = categories[selectedCategory]?.type === 'personalities_static';
    // Pour les quiz "Qui est qui" basés sur l'actu, utiliser FR par défaut
    const isWhoIsWhoNews = categories[selectedCategory]?.type === 'personalities_news';
    
    if (!isStaticQuiz && !isWhoIsWhoNews && !selectedCountry) return;
    
    try {
      setGenerating(true);
      setError('');
      
      // Récupérer ou générer le quiz
      let countryParam = selectedCountry || 'FR';
      if (isStaticQuiz) countryParam = 'AO';
      if (isWhoIsWhoNews) countryParam = 'FR';
      
      const response = await api.get(`/quiz/daily?category=${selectedCategory}&country=${countryParam}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        // Naviguer vers la page de quiz avec les paramètres
        navigate('/quiz', { 
          state: { 
            quiz: response.data,
            category: selectedCategory,
            country: countryParam,
            isStatic: response.data.isStatic || false
          } 
        });
      } else {
        setError('Pas assez d\'actualités pour générer un quiz. Réessayez plus tard.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement du quiz');
    } finally {
      setGenerating(false);
    }
  };

  // Vérifier si la catégorie nécessite une sélection de pays
  const needsCountrySelection = (categoryId) => {
    const cat = categories[categoryId];
    if (!cat) return true;
    // Pas de sélection de pays pour les quiz "Qui est qui"
    if (cat.type === 'personalities_static' || cat.type === 'personalities_news') return false;
    return true;
  };

  const isQuizGenerated = (category, country) => {
    const catData = availableQuizzes[category];
    if (!catData) return false;
    const countryData = catData.countries?.[country];
    return countryData?.generated || false;
  };

  const getQuestionCount = (category, country) => {
    const catData = availableQuizzes[category];
    if (!catData) return 0;
    const countryData = catData.countries?.[country];
    return countryData?.questionCount || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
        <AppHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <AppHeader />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="comic-button bg-gray-200 text-gray-700 px-4 py-2"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline mr-2" /> Retour
          </button>
          <div>
            <h1 className="comic-title text-3xl" style={{ color: '#1B4965' }}>
              Quiz du Jour
            </h1>
            <p className="text-gray-600">
              Choisissez une catégorie et un pays pour commencer
            </p>
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6"
          >
            {error}
          </motion.div>
        )}

        {/* Sélection du pays */}
        <div className="comic-panel mb-8">
          <h2 className="comic-text text-xl mb-4" style={{ color: '#1B4965' }}>
            <FaGlobe className="inline mr-2" /> Choisissez votre pays
          </h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(countries).map(([code, country]) => (
              <motion.button
                key={code}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCountry(code)}
                className={`px-4 py-3 rounded-lg border-2 font-bold transition-all ${
                  selectedCountry === code
                    ? 'bg-blue-600 text-white border-blue-800'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
                data-testid={`country-${code}`}
              >
                <span className="text-2xl mr-2">{country.flag}</span>
                {country.name}
              </motion.button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-3">
            <FaInfoCircle className="inline mr-1" />
            Les quiz sont générés à partir des actualités de chaque pays
          </p>
        </div>

        {/* Sélection de la catégorie */}
        <div className="comic-panel mb-8">
          <h2 className="comic-text text-xl mb-4" style={{ color: '#1B4965' }}>
            <FaNewspaper className="inline mr-2" /> Choisissez une catégorie
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Object.entries(categories).map(([id, cat]) => {
              const IconComponent = CATEGORY_ICONS[id] || FaNewspaper;
              const color = CATEGORY_COLORS[id] || '#1B4965';
              const isStatic = cat.type === 'personalities_static';
              const isGenerated = isStatic || isQuizGenerated(id, selectedCountry);
              const questionCount = isStatic ? 15 : getQuestionCount(id, selectedCountry);
              
              return (
                <motion.button
                  key={id}
                  whileHover={{ scale: 1.03, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedCategory(id)}
                  className={`relative p-5 rounded-xl border-3 text-left transition-all ${
                    selectedCategory === id
                      ? 'border-black shadow-lg'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ 
                    background: selectedCategory === id 
                      ? `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`
                      : 'white'
                  }}
                  data-testid={`category-${id}`}
                >
                  {/* Badge si statique ou généré */}
                  {isStatic ? (
                    <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <FaUserTie className="text-xs" /> Statique
                    </div>
                  ) : isGenerated ? (
                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <FaCheck className="text-xs" /> Prêt
                    </div>
                  ) : null}
                  
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                    style={{ background: color }}
                  >
                    <IconComponent className="text-2xl text-white" />
                  </div>
                  
                  <h3 className="font-bold text-lg mb-1" style={{ color }}>
                    {cat.name}
                  </h3>
                  
                  <p className="text-xs text-gray-500">
                    {isStatic 
                      ? 'Quiz personnalités'
                      : isGenerated 
                        ? `${questionCount} questions disponibles`
                        : 'Quiz à générer'
                    }
                  </p>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Résumé et bouton de lancement */}
        <AnimatePresence>
          {selectedCategory && (needsCountrySelection(selectedCategory) ? selectedCountry : true) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="comic-panel text-center"
              style={{ 
                background: `linear-gradient(135deg, ${CATEGORY_COLORS[selectedCategory]}15 0%, ${CATEGORY_COLORS[selectedCategory]}30 100%)` 
              }}
            >
              {categories[selectedCategory]?.type === 'personalities_static' ? (
                <>
                  <h3 className="comic-text text-xl mb-2">
                    🌍 {categories[selectedCategory]?.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Quiz statique avec 15 personnalités africaines et internationales
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Politique, Culture, Sport, Économie
                  </p>
                </>
              ) : categories[selectedCategory]?.type === 'personalities_news' ? (
                <>
                  <h3 className="comic-text text-xl mb-2">
                    🇫🇷 {categories[selectedCategory]?.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Qui sont les personnalités de l'actualité du jour ?
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Généré à partir des dernières actualités françaises et internationales
                  </p>
                </>
              ) : (
                <>
                  <h3 className="comic-text text-xl mb-2">
                    {countries[selectedCountry]?.flag} {categories[selectedCategory]?.name} - {countries[selectedCountry]?.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {isQuizGenerated(selectedCategory, selectedCountry) 
                      ? `Quiz prêt avec ${getQuestionCount(selectedCategory, selectedCountry)} questions`
                      : 'Le quiz sera généré à partir des dernières actualités'
                    }
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Sources : {countries[selectedCountry]?.sources}
                  </p>
                </>
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStartQuiz}
                disabled={generating}
                className="comic-button comic-button-red text-xl px-8 py-4"
                style={{ 
                  background: generating 
                    ? '#9CA3AF' 
                    : `linear-gradient(135deg, ${CATEGORY_COLORS[selectedCategory]} 0%, ${CATEGORY_COLORS[selectedCategory]}CC 100%)`
                }}
                data-testid="start-quiz-button"
              >
                {generating ? (
                  <>
                    <FaSpinner className="inline mr-2 animate-spin" />
                    {categories[selectedCategory]?.type === 'personalities_static' ? 'Chargement...' : 'Génération du quiz...'}
                  </>
                ) : (
                  <>
                    <FaPlay className="inline mr-2" />
                    Commencer le quiz
                  </>
                )}
              </motion.button>
              
              {!categories[selectedCategory]?.type?.includes('static') && !isQuizGenerated(selectedCategory, selectedCountry) && (
                <p className="text-xs text-gray-400 mt-3">
                  La première génération peut prendre quelques secondes
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info sur les quiz */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Les quiz sont renouvelés chaque jour à minuit.
            <br />
            Généré à la demande par le premier élève qui choisit une catégorie.
          </p>
        </div>
      </div>
    </div>
  );
}

export default QuizSelectionPage;
